import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Clock, ArrowRight, Layers, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SEO from "../components/SEO";

export const COURSE_CATEGORY_LABELS = {
    "firearm-safety": "Firearm safety",
    "hunter-safety": "Hunter safety",
    "outdoor-skills": "Outdoor skills",
    "first-aid": "First aid",
    "financial-literacy": "Financial literacy",
    "civics": "Civics",
    "digital-skills": "Digital skills",
    "life-skills": "Life skills",
    "other": "Other",
};

const DIFFICULTY_BADGE = {
    beginner: "bg-green-100 text-green-800",
    intermediate: "bg-amber-50 text-amber-800",
    advanced: "bg-red-50 text-[#E53935]",
};

const CourseCard = ({ course }) => (
    <Link
        to={`/learn/${course.slug}`}
        data-testid={`course-card-${course.slug}`}
        className="group bg-white border border-black/10 rounded-2xl overflow-hidden hover:border-[#E53935]/40 hover:shadow-md transition flex flex-col"
    >
        {course.cover_image ? (
            <img src={course.cover_image} alt={course.title} className="w-full h-40 object-cover" />
        ) : (
            <div className="w-full h-40 bg-[#F2D8D5]/50 flex items-center justify-center">
                <GraduationCap className="w-14 h-14 text-[#E53935]" strokeWidth={1} />
            </div>
        )}
        <div className="p-5 flex flex-col flex-1">
            <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">{COURSE_CATEGORY_LABELS[course.category] || course.category}</span>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${DIFFICULTY_BADGE[course.difficulty] || "bg-gray-100"}`}>{course.difficulty}</span>
            </div>
            <h3 className="font-heading text-xl text-[#1C252E] leading-tight group-hover:text-[#E53935] transition-colors">{course.title}</h3>
            {course.summary && <p className="text-sm text-[#4A5568] mt-2 line-clamp-2 flex-1">{course.summary}</p>}
            <div className="flex items-center gap-3 text-xs text-[#4A5568] mt-3 pt-3 border-t border-black/5">
                <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" strokeWidth={1.5} /> {course.lesson_count} lessons</span>
                {course.estimated_minutes > 0 && (
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={1.5} /> {course.estimated_minutes} min</span>
                )}
                {course.instructor && <span className="truncate">· {course.instructor}</span>}
            </div>
            <p className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#E53935] group-hover:gap-2 transition-all">Start learning <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /></p>
        </div>
    </Link>
);

const Learn = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState("");

    useEffect(() => {
        api.get("/courses", { params: category ? { category } : {} })
            .then((r) => setRows(r.data || []))
            .catch(() => toast.error("Could not load courses"))
            .finally(() => setLoading(false));
    }, [category]);

    return (
        <div className="min-h-screen bg-[#FDFBF7]">
            <SEO title="Continuing education" description="Free community courses — hunter safety, firearm safety, first aid, financial literacy, and more." path="/learn" />
            <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
                <header className="mb-10">
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Free for the community</p>
                    <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight leading-[1.05]">Continuing education</h1>
                    <p className="text-base text-[#4A5568] mt-4 max-w-2xl">Bite-sized courses curated by the foundation — hunter safety, firearm safety, first aid, civics, financial literacy. Track your progress, get certificates of completion.</p>
                </header>

                <div className="flex gap-3 mb-8 flex-wrap">
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        data-testid="learn-category-select"
                        className="rounded-full border border-black/10 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                    >
                        <option value="">All categories</option>
                        {Object.entries(COURSE_CATEGORY_LABELS).map(([slug, label]) => (
                            <option key={slug} value={slug}>{label}</option>
                        ))}
                    </select>
                </div>

                {loading ? (
                    <p className="text-sm text-[#4A5568]">Loading…</p>
                ) : rows.length === 0 ? (
                    <div className="bg-white border border-black/10 rounded-2xl p-12 text-center" data-testid="learn-empty">
                        <BookOpen className="w-12 h-12 text-[#4A5568] mx-auto mb-3" strokeWidth={1.2} />
                        <p className="font-heading text-xl text-[#1C252E]">No courses published yet.</p>
                        <p className="text-sm text-[#4A5568] mt-2">The foundation is building the curriculum — check back soon.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="learn-list">
                        {rows.map((c) => <CourseCard key={c.id} course={c} />)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Learn;
